//Include Both Helper File with needed methods
import { getFirebaseBackend } from "../../../helpers/firebaseHelper";
import axios from "axios";
import { clearAgency } from "../Agency/reducer";
const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API_URL;
// action
import {
    apiError,
    loginSuccess
} from "./reducer";

import {
    postFakeLogin,
    postJwtLogin,
    postSocialLogin,
} from "../../../helpers/fakebackendHelper";
import axiosInstance from "src/common/axiosInstance";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Read the mfa_trust cookie set after a successful MFA verification. */
function getMfaTrustCookie(): string | null {
    const cookies = document.cookie.split(';');
    for (const c of cookies) {
        const parts = c.trim().split('=');
        if (parts[0] === 'mfa_trust') {
            return decodeURIComponent(parts.slice(1).join('='));
        }
    }
    return null;
}

// ─── Auth thunks ─────────────────────────────────────────────────────────────

export const loginUser: any = (user: any, history: any) => async (dispatch: any) => {
    try {
        // Attach the 3-day trust cookie (if present) so the backend can skip MFA
        const mfaTrustCookie = getMfaTrustCookie();
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
        if (mfaTrustCookie) {
            headers['X-MFA-Trust'] = mfaTrustCookie;
        }

        const data: any = await axios.post(
            `${LARAVEL_API_URL}/login`,
            { email: user.email, password: user.password },
            { headers }
        );

        // Client users must go through MFA before tokens are issued
        if (data?.mfa_required) {
            dispatch(apiError(''));
            return data;
        }

        if (data?.access_token) {
            dispatch(loginSuccess(data));
            dispatch(apiError(''));
            localStorage.setItem("authUser", JSON.stringify(data));
        }

        return data;
    } catch (error: any) {
        const message =
            error?.response?.data?.message ||
            error?.message ||
            "Invalid credentials";
        dispatch(apiError(message));
    }
};

export const loginById: any = (userId: string | number, history: any) => async (dispatch: any) => {
    try {
        localStorage.removeItem('alert_shown');
        const response = await axiosInstance.post(`/login-by-id`, { user_id: userId });
        const data = response.data;
        if (data) {
            localStorage.setItem("authUser", JSON.stringify(data));
            dispatch(loginSuccess(data));
            return data;
        }
    } catch (error) {
        console.error('Login failed:', error);
        dispatch(apiError(error));
    }
};

// ─── MFA thunks ──────────────────────────────────────────────────────────────

/**
 * Request an OTP be sent to the user's phone (sms) or email.
 * Returns { message, channel, masked } on success.
 * Returns { message, requires_mobile: true } when SMS is chosen but no number is on file.
 */
export const sendMfaCode: any = (mfaToken: string, channel: 'sms' | 'email') => async () => {
    const result: any = await axios.post(`${LARAVEL_API_URL}/mfa/send`, {
        mfa_token: mfaToken,
        channel,
    });
    return result;
};

/**
 * Save a new mobile number for the user, then send the SMS OTP.
 * Returns { message, masked } on success.
 */
export const saveMfaMobile: any = (mfaToken: string, mobile: string) => async () => {
    const result: any = await axios.post(`${LARAVEL_API_URL}/mfa/save-mobile`, {
        mfa_token: mfaToken,
        mobile,
    });
    return result;
};

/**
 * Verify the OTP code. On success:
 *  - dispatches loginSuccess and stores tokens in localStorage
 *  - writes the 3-day mfa_trust cookie to the browser
 *  - returns the full login payload ({ access_token, user, ... })
 */
export const verifyMfaCode: any = (
    mfaToken: string,
    code: string,
    channel: 'sms' | 'email'
) => async (dispatch: any) => {
    const data: any = await axios.post(`${LARAVEL_API_URL}/mfa/verify`, {
        mfa_token: mfaToken,
        code,
        channel,
    });

    if (data?.access_token) {
        dispatch(loginSuccess(data));
        dispatch(apiError(''));
        localStorage.setItem("authUser", JSON.stringify(data));

        // Write the 3-day trust cookie so the next login skips MFA on this device
        if (data.mfa_trust_token) {
            const expires = new Date();
            expires.setDate(expires.getDate() + 3);
            document.cookie = [
                `mfa_trust=${encodeURIComponent(data.mfa_trust_token)}`,
                `expires=${expires.toUTCString()}`,
                'path=/',
                'SameSite=Lax',
            ].join('; ');
        }
    }

    return data;
};

// ─── Other existing thunks ───────────────────────────────────────────────────

export const socialLogin: any = (data: any, history: any, type: any) => async (dispatch: any) => {
    try {
        let response;

        if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
            let fireBaseBackend = getFirebaseBackend();
            response = fireBaseBackend.socialLoginUser(data, type);
        } else {
            response = postSocialLogin(data);
        }

        const socialdata = await response;

        if (socialdata) {
            localStorage.setItem("authUser", JSON.stringify(socialdata));
            dispatch(loginSuccess(socialdata));
            history("/analytics");
        }
    } catch (error: any) {
        dispatch(apiError(error?.message || "Social login failed"));
    }
};

export const logoutUser: any = (history: any) => async (dispatch: any) => {
    try {
        localStorage.removeItem("authUser");
        localStorage.removeItem("tempAuthUser");
        localStorage.removeItem("selectedOrder");
        localStorage.removeItem("selectedStatus");
        localStorage.removeItem("startDate");
        localStorage.removeItem("endDate");
        localStorage.removeItem("selectedLineItems");
        localStorage.removeItem("selectedAgency");
        localStorage.removeItem("adminUser");
        localStorage.removeItem('alert_shown');
        localStorage.removeItem('tourSeen');
        // Dispatch the LOGOUT_USER action to reset the Redux store
        dispatch({ type: 'LOGOUT_USER' });
        dispatch(clearAgency());
        history("/login");
    } catch (error: any) {
        dispatch(apiError(error?.message || "Logout failed"));
    }
};
